import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  acceptInvitation,
  getInvitation,
  getSession,
  rejectInvitation,
  sendVerificationOtp,
  signOut,
  type VerifyOtpParams,
  verifyEmailOnlyOtp,
  verifyEmailOtp,
} from "./api";

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
  });
}

export function useSendOtpMutation() {
  return useMutation({
    mutationFn: sendVerificationOtp,
  });
}

export function useVerifyOtpMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: VerifyOtpParams) => {
      if (params.type === "email-verification") {
        return verifyEmailOnlyOtp(params);
      }
      return verifyEmailOtp(params);
    },
    onSuccess: async () => {
      const data = await getSession();
      if (data?.user?.organizations) {
        localStorage.setItem("orgId", data.user.organizations?.[0]?.id ?? "");
      }
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

export function useSignOutMutation() {
  return useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      // Clear any cached session data
      window.location.href = "/auth/login";
    },
  });
}

export function useGetInvitationQuery(token: string) {
  return useQuery({
    queryKey: ["invitation", token],
    queryFn: () => getInvitation(token),
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvitationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: async () => {
      const data = await getSession();
      if (data?.user?.organizations?.[0]) {
        localStorage.setItem("orgId", data.user.organizations[0].id ?? "");
      }
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

export function useRejectInvitationMutation() {
  return useMutation({
    mutationFn: rejectInvitation,
  });
}
